import type { ReactNode } from "react";
import { cn } from "./cn";
import { Ikon, type IkonAdi } from "./Ikon";

export interface KartProps {
  baslik?: ReactNode;
  /**
   * Basligin HTML seviyesi — varsayilan 2 (UI/UX Dalga 1, axe G1).
   *
   * Once SABIT `<h3>` idi: sayfa basligi `<h1>` oldugu icin her ekranda
   * h1 → h3 atlamasi vardi (olculdu 05.10.2026: axe heading-order 282 dugum,
   * 34 ekran — panel ihlallerinin buyuk cogunlugu). Sayfanin ana bolumu h2;
   * bir kartin ICINDEKI kart ya da alt bolum 3 verir.
   */
  baslikSeviyesi?: 2 | 3 | 4;
  aciklama?: ReactNode;
  /** Basligin sagina yerlesen eylem — dugme, rozet */
  eylem?: ReactNode;
  /**
   * Vurgu tonu. Varsayilan notr.
   *
   * `uyari`/`hata`/`onay` YALNIZCA durum bildiren kartlarda. Bir
   * kartin rengini "dikkat cekmek icin" degistirmek, gercek uyarilari
   * degersizlestiriyor: her sey vurguluysa hicbir sey vurgulu degil.
   */
  ton?: "notr" | "uyari" | "hata" | "onay";
  /**
   * Hangi HTML ogesi olarak cizilecek.
   *
   * ================================================================
   * LISTE KARTI `<li>` OLMAK ZORUNDA
   * ================================================================
   * Varsayilan `section`, tek basina duran kartlar icin dogru. Ama
   * kartlarin cogu bir LISTE icinde yasiyor ve `<ul>` icine
   * `<section>` koymak GECERSIZ HTML: tarayici ogeyi listenin
   * disina tasiyabiliyor, ekran okuyucu da "3 ogeli liste" yerine
   * baska bir sey duyuruyor.
   *
   * Bu secenek olmadan liste kartlari bileseni HIC kullanamiyordu —
   * yani bilesen varken tekrar devam ediyordu.
   */
  olarak?: "section" | "div" | "li";
  /**
   * Kenar rengiyle DURUM isareti.
   *
   * ================================================================
   * KENAR RENGI TEK SINYAL DEGIL
   * ================================================================
   * Bu kalip dort ekranda tekrarliyordu: gecikmis tahsilat kirmizi
   * kenar, okunmamis duyuru marka kenari, bugunun gunu marka kenari.
   *
   * Renk YALNIZ BASINA kullanilmiyor ve kullanilMAMALI: her birinde
   * karti icinde durum ayrica YAZIYOR ("Gecikmis" rozeti, "Okundu
   * isaretle" dugmesi). Kenar, listede hizli tararken gozu
   * yonlendiriyor — bilgiyi o tasimiyor.
   *
   * Yalnizca kenarla anlatilan bir durum eklenirse renk korlugunde
   * kaybolur; o zaman metin de eklenmeli.
   */
  vurgu?: "yok" | "dikkat" | "marka";
  /**
   * Görünüm. `unk`: UNİKOÇ bölüm kartı (sürüm 9a) — yarıçap 24 px, mor-kenar çerçeve, UNİKOÇ gölgesi,
   * başlıkta mor ikon. Varsayılan `sade`: mevcut kartlar (giriş ekranı, öğrenci uygulaması) değişmez.
   * `ton`/`vurgu` `unk` görünümünde de çalışır (durum kartı hâlâ durum rengini taşır).
   */
  gorunum?: "sade" | "unk";
  /** Başlığın solundaki küçük mor ikon (yalnız `unk` görünümünde çizilir). */
  ikon?: IkonAdi;
  /** Kartı ekran okuyucuya adlandırır (başlıksız liste kartı). */
  "aria-label"?: string;
  children?: ReactNode;
  className?: string;
}

const VURGU: Record<NonNullable<KartProps["vurgu"]>, string> = {
  yok: "",
  dikkat: "border-hata-500",
  marka: "border-marka-500",
};

const TON: Record<NonNullable<KartProps["ton"]>, string> = {
  // `bg-notr-0`, `bg-white` DEGIL: beyaz da bir palet tonu ve `white`
  // yazmak marka disi bir beyaz kacagini gorunmez kilar (tokens.css).
  notr: "border-notr-200 bg-notr-0",
  uyari: "border-uyari-200 bg-uyari-50",
  hata: "border-hata-200 bg-hata-50",
  onay: "border-onay-200 bg-onay-50",
};

/**
 * KART
 *
 * ================================================================
 * AYRIM KENARLA, GOLGEYLE DEGIL
 * ================================================================
 * Bu ekranlar yogun: tablolar, filtre gruplari, sayi sutunlari.
 * Belirgin golgeli kartlar yan yana geldiginde ortaya "kabartma"
 * bir yuzey cikiyor ve goz her kartin sinirini ayrica cozmek
 * zorunda kaliyor. Ince kenar + cok hafif golge ayni ayrimi
 * gurultusuz yapiyor.
 *
 * Bu kalip on kusur ekranda ELLE tekrarlaniyordu
 * (`rounded-lg border border-notr-200 bg-white p-4`). Tekrar
 * kotu degil; TUTARSIZ tekrar kotu — biri `p-3`, oteki `p-4`,
 * bir digeri `rounded-xl` yazmisti.
 */
export function Kart({
  baslik,
  aciklama,
  eylem,
  ton = "notr",
  vurgu = "yok",
  olarak: Oge = "section",
  baslikSeviyesi = 2,
  gorunum = "sade",
  ikon,
  "aria-label": ariaEtiketi,
  children,
  className,
}: KartProps) {
  const Baslik = `h${baslikSeviyesi}` as const;
  const unk = gorunum === "unk";
  return (
    <Oge
      data-ui="kart"
      aria-label={ariaEtiketi}
      className={cn(
        /*
          GOLGE `shadow-kart`, `shadow-[--shadow-kart]` DEGIL.

          Ikincisi Tailwind v3 sozdizimi. v4 onu degisken BASVURUSU
          saymiyor; uretilen kural `--tw-shadow: --shadow-kart` oluyor —
          gecersiz bir deger, yani `box-shadow` HIC CIZILMIYOR. Hicbir
          hata yok, konsol temiz, kart yalnizca golgesiz duruyor.
          Derleyiciyle olculdu 04.09.2026. `--shadow-kart` zaten
          `@theme` icinde oldugu icin Tailwind `shadow-kart` yardimci
          sinifini kendisi uretiyor; dogru yazim o.
        */
        /*
          IC BOSLUK DEGISKENDEN (04.10.2026): varsayilan 1rem — eski `p-4` ile AYNI piksel. Yonetim paneli
          kendi kapsaminda (`.panel-yuzeyi`, apps/panel/src/stil.css) `--kart-bosluk`u Hizli Okuma
          olcusune cekiyor; ogrenci uygulamasi ve giris ekrani degismiyor. Cagiranin `className`inda
          verdigi `p-*` yine kazanir (tailwind-merge ayni ozellik grubunu tanir).
        */
        unk
          ? "rounded-[1.5rem] border p-4 shadow-(--golge-unk-kart) md:p-6"
          : "rounded-kart shadow-kart border p-[var(--kart-bosluk,1rem)]",
        unk && ton === "notr" ? "bg-notr-0 border-mor-kenar" : TON[ton],
        // Vurgu tonun kenarini EZIYOR — sirasi bilerek sonra.
        VURGU[vurgu],
        className,
      )}
    >
      {(baslik || eylem) && (
        <div
          className={cn(
            "flex justify-between gap-3",
            unk ? "mb-4 flex-wrap items-center gap-x-4 gap-y-2" : "mb-3 items-start",
          )}
        >
          <div className="min-w-0">
            {baslik && (
              <Baslik
                className={cn(
                  unk
                    ? "text-metin-koyu flex items-center gap-2 text-base font-bold"
                    : "text-notr-800 text-sm font-semibold",
                )}
              >
                {unk && ikon && <Ikon ad={ikon} className="text-mor-500 size-5" />}
                {baslik}
              </Baslik>
            )}
            {aciklama && (
              <p
                className={cn(
                  unk ? "text-metin-ikincil mt-1 text-sm" : "text-notr-600 mt-0.5 text-xs",
                )}
              >
                {aciklama}
              </p>
            )}
          </div>
          {/* Eylem SAGDA ve kucultulmuyor: baslik uzarsa o kirpilir,
              dugme degil — tiklanamayan bir dugme, uzun bir basliktan
              daha kotu. */}
          {eylem && (
            <div className={cn(unk ? "text-metin-ikincil min-w-0 text-sm" : "shrink-0")}>
              {eylem}
            </div>
          )}
        </div>
      )}
      {children}
    </Oge>
  );
}
